// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useLayoutEffect, useMemo, useState, useEffect, useRef } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { EVENT_NAMES } from '../../utils/eventLogging/eventNames';
import { DateRangePicker } from 'react-date-range';
import { endOfDay, endOfMonth, format, isSameDay, startOfMonth, startOfYear, subMonths } from 'date-fns';
import { createPortal } from 'react-dom';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './TimeFrameFilter.scss';
import { useTranslation } from '../../i18n/i18n';
import { useTheme } from '../../context/ThemeContext';
import { cn } from '../../utils/cn';
import { ChevronDown } from 'lucide-react';
import CalendarNavigator from '../ui/CalendarNavigator';
import { getDateFnsLocale } from '../../i18n/i18n';

const TIMEFRAME_DISPLAY_DATE_FORMAT = 'dd. MMM yyyy';

function formatTimeframeRange(start, end, locale) {
  if (!start) return '';
  const formatOptions = { locale };
  const startLabel = format(start, TIMEFRAME_DISPLAY_DATE_FORMAT, formatOptions);
  if (!end || isSameDay(start, end)) return startLabel;
  return `${startLabel} - ${format(end, TIMEFRAME_DISPLAY_DATE_FORMAT, formatOptions)}`;
}

function createIsSelected(rangeFn) {
  return function isSelected(range) {
    const candidate = rangeFn();
    return isSameDay(candidate.startDate, range?.startDate) && isSameDay(candidate.endDate, range?.endDate);
  };
}

export default function TimeframeFilter({ timeframe, setTimeframe }) {
  const { currentUser, logEventCustom } = React.useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const { uiMode } = useTheme() ?? {};
  const isDark = uiMode === 'dark-mode';
  const dateFnsLocale = getDateFnsLocale(currentUser?.info?.languageCode);
  const [showCalendar, setShowCalendar] = useState(false);
  const [dateState, setDateState] = useState([
    {
      startDate: timeframe?.startDate || new Date(),
      endDate: timeframe?.endDate || new Date(),
      key: 'selection',
    },
  ]);
  const pickerContainerRef = useRef(null);
  const filterRef = useRef(null);
  const [footerTarget, setFooterTarget] = useState(null);

  const selection = dateState?.[0];

  const deviceInitialization =
    currentUser?.devices?.[currentUser.device_id]?.device_initialization?.toDate?.() ?? undefined;

  const timeframePresets = useMemo(() => {
    const presets = [
      {
        label_code: 'timeframe_today',
        label: t('timeframe_today'),
        range: () => ({
          startDate: new Date(new Date().setHours(0, 0, 0, 0)),
          endDate: endOfDay(new Date()),
        }),
      },
      {
        label_code: 'timeframe_yesterday',
        label: t('timeframe_yesterday'),
        range: () => {
          const yesterday = new Date(new Date().setDate(new Date().getDate() - 1));
          yesterday.setHours(0, 0, 0, 0);
          return {
            startDate: yesterday,
            endDate: endOfDay(yesterday),
          };
        },
      },
      {
        label_code: 'timeframe_this_week',
        label: t('timeframe_this_week'),
        range: () => {
          const today = new Date();
          const firstDay = new Date(today);
          const day = today.getDay();
          const diff = today.getDate() - day + (day === 0 ? -6 : 1);
          firstDay.setDate(diff);
          firstDay.setHours(0, 0, 0, 0);
          return {
            startDate: firstDay,
            endDate: endOfDay(today),
          };
        },
      },
      {
        label_code: 'timeframe_last_week',
        label: t('timeframe_last_week'),
        range: () => {
          const today = new Date();
          const day = today.getDay();
          const daysToLastSunday = day === 0 ? 7 : day;
          const lastWeekEnd = new Date(today);
          lastWeekEnd.setDate(today.getDate() - daysToLastSunday);
          lastWeekEnd.setHours(23, 59, 59, 999);

          const lastWeekStart = new Date(lastWeekEnd);
          lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
          lastWeekStart.setHours(0, 0, 0, 0);

          return { startDate: lastWeekStart, endDate: lastWeekEnd };
        },
      },
      {
        label_code: 'timeframe_this_month',
        label: t('timeframe_this_month'),
        range: () => ({
          startDate: startOfMonth(new Date()),
          endDate: endOfDay(new Date()),
        }),
      },
      {
        label_code: 'timeframe_last_month',
        label: t('timeframe_last_month'),
        range: () => {
          const lastMonth = subMonths(new Date(), 1);
          return {
            startDate: startOfMonth(lastMonth),
            endDate: endOfMonth(lastMonth),
          };
        },
      },
      {
        label_code: 'timeframe_last_three_months',
        label: t('timeframe_last_three_months'),
        range: () => ({
          startDate: startOfMonth(subMonths(new Date(), 3)),
          endDate: endOfMonth(new Date()),
        }),
      },
      {
        label_code: 'timeframe_this_year',
        label: t('timeframe_this_year'),
        range: () => ({
          startDate: startOfYear(new Date()),
          endDate: endOfDay(new Date()),
        }),
      },
    ];

    if (deviceInitialization) {
      presets.push({
        label_code: 'timeframe_all',
        label: t('timeframe_all'),
        range: () => ({
          startDate: deviceInitialization,
          endDate: endOfDay(new Date()),
        }),
      });
    }

    return presets;
  }, [t, deviceInitialization]);

  const staticRanges = useMemo(
    () =>
      timeframePresets.map((preset) => ({
        label: preset.label,
        label_code: preset.label_code,
        range: preset.range,
        isSelected: createIsSelected(preset.range),
      })),
    [timeframePresets],
  );

  // Close when clicking outside the trigger + calendar popover.
  useEffect(() => {
    if (!showCalendar) return undefined;

    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCalendar]);

  useLayoutEffect(() => {
    if (!showCalendar) return;
    setFooterTarget(pickerContainerRef.current?.querySelector('.rdrMonths.rdrMonthsVertical') ?? null);
  }, [isDark, showCalendar, selection?.startDate, selection?.endDate]);

  const handleDateRangeChange = (item) => {
    const nextSelection = item?.selection;
    if (!nextSelection?.startDate || !nextSelection?.endDate) return;

    const nextStartDate = nextSelection.startDate;
    const nextEndDate = endOfDay(nextSelection.endDate);

    setDateState([
      {
        ...nextSelection,
        startDate: nextStartDate,
        endDate: nextEndDate,
      },
    ]);

    const matchingPreset = staticRanges.find((preset) => {
      if (typeof preset?.range !== 'function') return false;
      const candidate = preset.range();
      return isSameDay(candidate?.startDate, nextStartDate) && isSameDay(candidate?.endDate, nextEndDate);
    });

    const label = matchingPreset?.label_code ?? 'custom_range';

    setTimeframe({
      startDate: nextStartDate,
      endDate: nextEndDate,
      label,
    });

    if (logEventCustom) {
      logEventCustom(EVENT_NAMES.FILTER, {
        type: 'timeframe',
        value: label,
        start_date: nextStartDate.toISOString(),
        end_date: nextEndDate.toISOString(),
      });
    }
  };

  const matchingPreset = useMemo(
    () => timeframePresets.find((opt) => opt.label_code === timeframe?.label),
    [timeframePresets, timeframe?.label],
  );

  const isCustomRange = !matchingPreset;

  const buttonRangeLabel = useMemo(() => {
    if (matchingPreset) return matchingPreset.label;
    return formatTimeframeRange(timeframe?.startDate, timeframe?.endDate, dateFnsLocale);
  }, [matchingPreset, timeframe?.startDate, timeframe?.endDate, dateFnsLocale]);

  const selectedRangeLabel = useMemo(
    () => formatTimeframeRange(selection?.startDate, selection?.endDate, dateFnsLocale),
    [selection?.startDate, selection?.endDate, dateFnsLocale],
  );

  return (
    <div className='relative flex items-center' ref={filterRef}>
      <div
        className={cn(
          'relative inline-flex items-center rounded-xl p-0 min-h-[44px] text-base shrink-0',
          'bg-white border border-[#e1e5eb] text-[#2d3041]',
          'dark:bg-[#0c121d] dark:border-[#3c3e53] dark:text-[#fafafa]',
          'hover:border-[#cfd6df] dark:hover:border-[#4a4d66]',
          isCustomRange ? 'w-max max-w-full min-w-[17.5rem]' : 'w-[17.5rem] min-w-[17.5rem] max-w-[17.5rem]',
        )}
      >
        <button
          type='button'
          className='inline-flex items-center gap-2.5 w-full min-h-[44px] border-0 bg-transparent py-[10px] px-3 rounded-xl text-base text-inherit cursor-pointer min-w-0 hover:!bg-transparent active:!bg-transparent focus:outline-none'
          onClick={() => setShowCalendar((prev) => !prev)}
        >
          <span className='inline-flex items-center opacity-75 whitespace-nowrap text-base text-[#8a8c95] dark:text-[#90a1b9]'>
            {t('timeframe_title')}
          </span>
          <span
            className={cn(
              'text-base font-medium leading-5 text-left',
              isCustomRange ? 'whitespace-nowrap' : 'flex-1 min-w-0 truncate',
            )}
          >
            {buttonRangeLabel}
          </span>
          <ChevronDown
            className={cn('shrink-0 opacity-70 transition-transform', showCalendar && 'rotate-180')}
            size={18}
          />
        </button>
      </div>

      {showCalendar && (
        <div className='calendar-container' ref={pickerContainerRef}>
          <DateRangePicker
            editableDateInputs={true}
            onChange={handleDateRangeChange}
            moveRangeOnFirstSelection={false}
            ranges={dateState}
            months={1}
            staticRanges={staticRanges}
            inputRanges={[]}
            showMonthAndYearPickers={true}
            showDateDisplay={false}
            navigatorRenderer={CalendarNavigator}
            locale={dateFnsLocale}
            className={cn('date-range-picker inline-flex gap-5', isDark && 'date-range-picker--dark')}
          />

          {footerTarget &&
            createPortal(
              <div className='date-range-picker__footer'>
                <span className='date-range-picker__footer-label'>{t('date_selected')}</span>
                <span className='date-range-picker__footer-value'>{selectedRangeLabel}</span>
              </div>,
              footerTarget,
            )}
        </div>
      )}
    </div>
  );
}
